import { HttpClient } from 'effect/unstable/http'
import { createSTClientSDK, type STSDK } from '@st/sdk'
import { Context, Effect, Layer, Stream } from 'effect'
import { makeAuthClient, type AuthClient } from '@features/auth/st-auth-client'
import { ErrorReporting } from './error-reporting'
import { BrowserCookies } from './browser-cookies'
import { IdGenerator } from './id-generator'
import { RuntimeConfig } from './runtime-config'
import { LOCAL_GRANTS_COOKIE_NAME } from '~/features/st-questionnaire/client-mfa-gate-logic'

export class Auth extends Context.Service<Auth, AuthClient>()('Auth') {}

/** Current and future auth status from the scoped Auth service. */
export const observeAuthStatus = Stream.unwrap(
  Effect.map(Effect.service(Auth), (auth) => auth.statuses)
)

type AuthSdkFactoryService = {
  create: (args: {
    baseUrl: string
    httpClient: HttpClient.HttpClient
    getRequestHeaders: () => Effect.Effect<Record<string, string>, unknown, never>
    makeRequestId: Effect.Effect<string>
  }) => STSDK
}

export class AuthSdkFactory extends Context.Service<AuthSdkFactory, AuthSdkFactoryService>()(
  'AuthSdkFactory'
) {}

export const authSdkFactoryLiveLayer = Layer.succeed(AuthSdkFactory, {
  create: ({ baseUrl, httpClient, getRequestHeaders, makeRequestId }) =>
    createSTClientSDK({
      baseUrl,
      httpClient,
      getRequestHeaders,
      makeRequestId
    })
})

export function authLayer(authClient: AuthClient) {
  return Layer.succeed(Auth, authClient)
}

export function authLiveLayer() {
  return Layer.effect(
    Auth,
    Effect.gen(function* () {
      const config = yield* RuntimeConfig
      const httpClient = yield* HttpClient.HttpClient
      const sdkFactory = yield* AuthSdkFactory
      const errorReporting = yield* ErrorReporting
      const cookies = yield* BrowserCookies
      const idGenerator = yield* IdGenerator

      const authClient = yield* makeAuthClient({
        oauthRedirectBaseUrl: config.browserOrigin,
        apiBaseUrl: config.apiBaseUrl,
        httpClient,
        makeRequestId: idGenerator.generate,
        clearLocalGrantsCookie: cookies.write(
          `${LOCAL_GRANTS_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax`
        ),
        createSdk: (getRequestHeaders) =>
          sdkFactory.create({
            baseUrl: config.apiBaseUrl,
            httpClient,
            getRequestHeaders,
            makeRequestId: idGenerator.generate
          }),
        errorReporting
      })

      yield* Effect.addFinalizer(() => authClient.shutdown())

      return authClient
    })
  )
}
